import type { Role } from '../types/database';
import type { GroupRoleTone } from '../types/groups.types';
import { formatUserRole } from './users.utils';

/**
 * Role chip colors are stored as a short token (e.g. "purple") on
 * roles.chip_color. This keeps the database free of Tailwind class strings and
 * lets the palette evolve in code. Each token maps to a pill tone (used in
 * lists) and a solid swatch (used in the color picker).
 */
export type RoleColorTone = {
  token: string;
  label: string;
  pillClassName: string;
  dotClassName: string;
  swatchClassName: string;
};

export const ROLE_COLOR_OPTIONS: RoleColorTone[] = [
  {
    token: 'purple',
    label: 'Purple',
    pillClassName: 'border-purple-400/40 text-purple-200',
    dotClassName: 'bg-purple-400',
    swatchClassName: 'bg-purple-400',
  },
  {
    token: 'blue',
    label: 'Blue',
    pillClassName: 'border-blue-400/40 text-blue-200',
    dotClassName: 'bg-blue-400',
    swatchClassName: 'bg-blue-400',
  },
  {
    token: 'sky',
    label: 'Sky',
    pillClassName: 'border-sky-400/40 text-sky-200',
    dotClassName: 'bg-sky-400',
    swatchClassName: 'bg-sky-400',
  },
  {
    token: 'green',
    label: 'Green',
    pillClassName: 'border-green-400/40 text-green-200',
    dotClassName: 'bg-green-400',
    swatchClassName: 'bg-green-400',
  },
  {
    token: 'teal',
    label: 'Teal',
    pillClassName: 'border-teal-400/40 text-teal-200',
    dotClassName: 'bg-teal-400',
    swatchClassName: 'bg-teal-400',
  },
  {
    token: 'amber',
    label: 'Amber',
    pillClassName: 'border-amber-400/40 text-amber-200',
    dotClassName: 'bg-amber-400',
    swatchClassName: 'bg-amber-400',
  },
  {
    token: 'rose',
    label: 'Rose',
    pillClassName: 'border-rose-400/40 text-rose-200',
    dotClassName: 'bg-rose-400',
    swatchClassName: 'bg-rose-400',
  },
  {
    token: 'slate',
    label: 'Slate',
    pillClassName: 'border-slate-400/40 text-slate-200',
    dotClassName: 'bg-slate-400',
    swatchClassName: 'bg-slate-400',
  },
];

export const DEFAULT_ROLE_COLOR = 'purple';

const FALLBACK_TONE: RoleColorTone = {
  token: 'slate',
  label: 'Slate',
  pillClassName: 'border-slate-400/40 text-slate-300',
  dotClassName: 'bg-slate-400',
  swatchClassName: 'bg-slate-400',
};

export function getRoleColorTone(
  token: string | null | undefined
): RoleColorTone {
  if (!token) {
    return FALLBACK_TONE;
  }
  return (
    ROLE_COLOR_OPTIONS.find((option) => option.token === token) ?? FALLBACK_TONE
  );
}

/**
 * Full tonal styling per color token, used by the group / event-audience
 * surfaces that need more than a pill (radios, section headers, checkboxes).
 * Class strings are written out literally so Tailwind can see them.
 */
const ROLE_TONES: Record<string, GroupRoleTone> = {
  purple: {
    pillClassName: 'border-purple-400/40 bg-purple-400/15 text-purple-300',
    radioClassName: 'border-purple-400/40 bg-purple-400/15 text-purple-300',
    radioDotClassName: 'bg-purple-400',
    sectionBorderClassName: 'border-purple-400/45',
    sectionHeaderClassName: 'bg-purple-400/15',
    sectionTextClassName: 'text-purple-300',
    selectedRowClassName: 'bg-purple-400/15',
    checkboxClassName:
      'border-purple-400 data-[state=checked]:bg-purple-400 data-[state=checked]:text-slate-700',
  },
  blue: {
    pillClassName: 'border-blue-400/40 bg-blue-400/15 text-blue-300',
    radioClassName: 'border-blue-400/40 bg-blue-400/15 text-blue-300',
    radioDotClassName: 'bg-blue-400',
    sectionBorderClassName: 'border-blue-400/45',
    sectionHeaderClassName: 'bg-blue-400/15',
    sectionTextClassName: 'text-blue-300',
    selectedRowClassName: 'bg-blue-400/15',
    checkboxClassName:
      'border-blue-400 data-[state=checked]:bg-blue-400 data-[state=checked]:text-slate-700',
  },
  sky: {
    pillClassName: 'border-sky-400/40 bg-sky-400/15 text-sky-300',
    radioClassName: 'border-sky-400/40 bg-sky-400/15 text-sky-300',
    radioDotClassName: 'bg-sky-400',
    sectionBorderClassName: 'border-sky-400/45',
    sectionHeaderClassName: 'bg-sky-400/15',
    sectionTextClassName: 'text-sky-300',
    selectedRowClassName: 'bg-sky-400/15',
    checkboxClassName:
      'border-sky-400 data-[state=checked]:bg-sky-400 data-[state=checked]:text-slate-700',
  },
  green: {
    pillClassName: 'border-green-400/40 bg-green-400/15 text-green-300',
    radioClassName: 'border-green-400/40 bg-green-400/15 text-green-300',
    radioDotClassName: 'bg-green-400',
    sectionBorderClassName: 'border-green-400/45',
    sectionHeaderClassName: 'bg-green-400/15',
    sectionTextClassName: 'text-green-300',
    selectedRowClassName: 'bg-green-400/15',
    checkboxClassName:
      'border-green-400 data-[state=checked]:bg-green-400 data-[state=checked]:text-slate-700',
  },
  teal: {
    pillClassName: 'border-teal-400/40 bg-teal-400/15 text-teal-300',
    radioClassName: 'border-teal-400/40 bg-teal-400/15 text-teal-300',
    radioDotClassName: 'bg-teal-400',
    sectionBorderClassName: 'border-teal-400/45',
    sectionHeaderClassName: 'bg-teal-400/15',
    sectionTextClassName: 'text-teal-300',
    selectedRowClassName: 'bg-teal-400/15',
    checkboxClassName:
      'border-teal-400 data-[state=checked]:bg-teal-400 data-[state=checked]:text-slate-700',
  },
  amber: {
    pillClassName: 'border-amber-400/40 bg-amber-400/15 text-amber-300',
    radioClassName: 'border-amber-400/40 bg-amber-400/15 text-amber-300',
    radioDotClassName: 'bg-amber-400',
    sectionBorderClassName: 'border-amber-400/45',
    sectionHeaderClassName: 'bg-amber-400/15',
    sectionTextClassName: 'text-amber-300',
    selectedRowClassName: 'bg-amber-400/15',
    checkboxClassName:
      'border-amber-400 data-[state=checked]:bg-amber-400 data-[state=checked]:text-slate-700',
  },
  rose: {
    pillClassName: 'border-rose-400/40 bg-rose-400/15 text-rose-300',
    radioClassName: 'border-rose-400/40 bg-rose-400/15 text-rose-300',
    radioDotClassName: 'bg-rose-400',
    sectionBorderClassName: 'border-rose-400/45',
    sectionHeaderClassName: 'bg-rose-400/15',
    sectionTextClassName: 'text-rose-300',
    selectedRowClassName: 'bg-rose-400/15',
    checkboxClassName:
      'border-rose-400 data-[state=checked]:bg-rose-400 data-[state=checked]:text-slate-700',
  },
  slate: {
    pillClassName: 'border-slate-400/40 bg-slate-400/15 text-slate-300',
    radioClassName: 'border-slate-400/40 bg-slate-400/15 text-slate-300',
    radioDotClassName: 'bg-slate-400',
    sectionBorderClassName: 'border-slate-400/45',
    sectionHeaderClassName: 'bg-slate-400/15',
    sectionTextClassName: 'text-slate-300',
    selectedRowClassName: 'bg-slate-400/15',
    checkboxClassName:
      'border-slate-400 data-[state=checked]:bg-slate-400 data-[state=checked]:text-slate-700',
  },
};

/**
 * Resolve the full tonal styling for a role color token, falling back to slate
 * for unknown or missing colors (e.g. a newly created role with no color yet).
 */
export function getRoleTone(token: string | null | undefined): GroupRoleTone {
  return (token && ROLE_TONES[token]) || ROLE_TONES.slate;
}

/**
 * Sort roles for display: system roles first, then by sort_order, then name.
 */
export function sortRolesForDisplay(roles: Role[]): Role[] {
  return [...roles].sort((a, b) => {
    if (a.is_system !== b.is_system) {
      return a.is_system ? -1 : 1;
    }
    if (a.sort_order !== b.sort_order) {
      return a.sort_order - b.sort_order;
    }
    return a.display_name.localeCompare(b.display_name);
  });
}

/**
 * Read-through registry of role metadata, keyed by role key. Synced once at the
 * authenticated app root (see useRoleRegistrySync) so the pure display helpers
 * below — getRoleToneByKey / getRoleLabelByKey / getOrderedRoleKeys — can resolve
 * a role's color, label, and order without every call site depending on a hook.
 * The root re-renders when the roles query resolves, which cascades to consumers.
 */
type RoleMeta = { colorToken: string | null; label: string };

let roleRegistry = new Map<string, RoleMeta>();

export function setRoleRegistry(roles: Role[]): void {
  roleRegistry = new Map(
    roles.map((role) => [
      role.key,
      { colorToken: role.chip_color, label: role.display_name },
    ])
  );
}

export function getOrderedRoleKeys(): string[] {
  return [...roleRegistry.keys()];
}

export function isKnownRoleKey(key: string): boolean {
  return roleRegistry.has(key);
}

export function getRoleToneByKey(
  key: string | null | undefined
): GroupRoleTone {
  return getRoleTone(key ? roleRegistry.get(key)?.colorToken : null);
}

export function getRoleColorLabelByKey(key: string | null | undefined): string {
  const token = key ? roleRegistry.get(key)?.colorToken : null;
  return getRoleColorTone(token).label;
}

export function getRoleLabelByKey(key: string | null | undefined): string {
  if (!key) {
    return formatUserRole(key);
  }
  return roleRegistry.get(key)?.label ?? formatUserRole(key);
}

export function filterRoles(roles: Role[], searchQuery: string): Role[] {
  const query = searchQuery.trim().toLowerCase();
  if (!query) {
    return roles;
  }
  return roles.filter(
    (role) =>
      role.display_name.toLowerCase().includes(query) ||
      role.key.toLowerCase().includes(query) ||
      (role.description?.toLowerCase().includes(query) ?? false)
  );
}
